'use client';

import { User } from '@benzinga/session';

const IGNORED_ERRORS = [
  // 1password can cause this error https://1password.community/discussion/139709/latest-chrome-extension-breaks-google-sign-in-on-our-website
  '[GSI_LOGGER]: Check credential status returns invalid response.',
  '[GSI_LOGGER]: The given origin is not allowed for the given client ID.',
  '[GSI_LOGGER]: FedCM get() rejects with NetworkError: Error retrieving a token.',
  '[GSI_LOGGER]: FedCM get() rejects with AbortError: signal is aborted without reason',
  '[GSI_LOGGER]: FedCM get() rejects with NotSupportedError: The user agent does not support public key credentials.',

  // Google Publisher Tag (GPT) / Google Ads errors
  /^\[GPT\].*/,
  /^Exception in queued GPT command.*/,
  /^Exception in googletag\.cmd function.*/,
  /.*googletag\.defineSlot.*AdThrive.*already associated with another slot.*/,
  /.*This is an outdated version of the Google Publisher Tag.*/,

  // React framework errors
  'Minified React error #418;',
  'Minified React error #421;',
  'Minified React error #422;',
  'Minified React error #425;',
  /Minified React error #185/,

  // Taboola third-party errors
  /.*taboola\.com.*/,
  /.*vidstat\.taboola\.com.*/,
  /.*sync\.taboola\.com.*/,
  /.*cdn\.taboola\.com.*/,

  // AdThrive errors
  /.*AdThrive.*/,
  /Converting circular structure to JSON[\s\S]*AdThrive[\s\S]*/,

  // Registration & Push notification errors
  /^Registration failed.*/,
  /.*Error retrieving push subscription.*/,
  /.*push service.*/,
  /.*Connection to web push daemon failed.*/,

  // Network & Fetch errors
  /^Failed to fetch$/,
  /^Failed to load$/,
  /^Load failed$/,
  /^NetworkError.*/,
  /^Network Error$/,
  /^Connection failed$/,
  /^Could not establish connection.*/,
  /^There has been a problem with your fetch operation.*/,
  /^Fetch is aborted$/,
  /^The operation was aborted\.?$/,
  /^signal is aborted without reason$/,

  // HTTP status code errors
  /^status -> 0$/,
  /^status -> 403$/,

  // XMLHttpRequest errors
  // /^XMLHttpRequest Error:.*/,
  // /.*Error processing XMLHttpRequest response:.*/,

  // JSON parsing errors
  /^Error parsing JSON.*/,
  /.*cyclic object value.*/,
  /^JSON\.stringify cannot serialize cyclic structures\.$/,
  /.*Error parsing cookie:.*not valid JSON.*/,

  // Video player errors
  /^Video initialization timeout.*/,
  /^\[video:init\].*/,
  /^Failed to initialize video player.*/,
  /^The play\(\) request was.*/,
  /.*play\(\) failed because.*/,
  /^\[KARGO\]\[CREATIVE\].*Error - timeout$/,
  /^VIDEOJS: ERROR:/,

  // Browser API errors
  /^ResizeObserver loop/,
  /.*Failed to execute '.*' on '.*':.*/,
  /.*External: Failed to override.*/,
  /.*The operation is not supported$/,
  /.*The media resource indicated by the src attribute.*/,

  // "is not defined" / "is not a function" errors
  'options is not defined',
  'model is not defined',
  'googletag is not defined',
  'TRC.getRTUS is not a function',
  /.*Can't find variable:.*/,

  // Null/undefined evaluation errors
  /.*null is not an object \(evaluating.*\)$/,
  /.*undefined is not an object \(evaluating.*\)$/,

  // Script injection/import errors
  /^Failed to inject .* script$/,
  /^Failed to import script/,

  // Third-party service errors
  /.*contextweb\.com.*/,
  /.*gum\.criteo\.com.*/,
  /.*script: https:\/\/bh\.contextweb\.com.*/,
  /^\/\* Blocked by AdGuard \*\/$/,
  /^WCT Server Unavailable/,
  /^apstag\.setDisplayBids called without specifying ad server$/,
  /^GPT load timed out after \d+ms$/,
  /^Error sending segment performance metrics/,
  /^Module load timeout: m_\d+$/,
  /^Invalid bid ID\./,
  /Failed to send consent strings to ad server/,
  /window\.googletag\.setConfig is not a function/,
  /^Failed to fetch recommended articles/,
  /^XMLHttpRequest Error: Network request failed$/,
  /^AIPES response code: 0$/,
  /^window\.ga is not a function$/,
  /^amznads is not defined$/,
  /reCAPTCHA placeholder element/,
  /^reCAPTCHA Timeout/,
  /^Error playing video:/,
  /^Oe\[_t\]\.getAttribute is not a function$/,
  /^Fe\[pt\]\.getAttribute is not a function$/,
  /^JSON\.parse: unexpected character/,
  /\[Ad Render Failure\] No selector found/,
  /^can't access property/,
  /window\.pbjs\.getConfig is not a function/,
  /^operation timed out$/,
  /^Failed to connect to MetaMask$/,
  /^Talisman extension.*/,
  /^User rejected the request$/,

  // Code splitting / chunk loading errors
  /^Loading chunk \d+ failed$/,
  /^Loading CSS chunk \d+ failed$/,

  // Generic errors
  /^Script error\.$/,
  /^Timeout$/,
  /^Maximum call stack size exceeded\.?$/,
  /^Invalid string length$/,
  /^Invalid or unexpected token$/,
  /^Unexpected token '<'$/,
  /^Attempting to use a disconnected port object$/,
  /^Blocked a frame with origin.*from accessing a cross-origin frame/,
  /.*Permission denied to access property.*on cross-origin object$/,
  /^The request is not alDlowed by the user agent.*/,
  /^The source window of session client post messages.*/,
  /^detectIncognito.*/,
  /^Unhandled promise rejection.*/,

  // Specific string matches (too unique for regex)
  'Warning: Intercom was not initialized. Please turn AdBlock off if you have it enabled.',
  'UID2 shared library - ',
  'Button config is missing',
  'skipping pixel true false',
  'Logger send request failed',
  'Password is required 1',
  'Email is required 0',
  '{}',
  'N/A',
  '{"reason":"script: ","isInfo":true}',
  "'' is not a valid selector.",
  'Invalid call to runtime.sendMessage(). Tab not found.',
  'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.',

  // Regex patterns for dynamic errors
  /.*origins don't match.*/,
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  /.*Failed to read a named property.*/,
  /.*Failed to execute 'insertBefore' on 'Node'.*/,
  /.*Failed to execute 'removeChild' on 'Node'.*/,
  /Node\.insertBefore: Child to insert before is not a child of this node/,
  /Node\.removeChild: The node to be removed is not a child of this node/,
  /.*ClsDisableAds::checkCommandQueue.*/,
  /.*Cannot read properties of undefined \(reading '.*'\).*/,
  /.*Cannot set properties of undefined \(setting '.*'\).*/,
  /.*Cannot read properties of null \(reading '.*'\).*/,
  /ERROR BOUNDARY[\s\S]*"error": \{\}/,
  /The object can not be found here\./,
  /Utils::waitUntil: max iteration count reached/,
  /Initiator \(\[Video \d+\]\) is not a Screen or a ScreenObject\./,
  'element.matches is not a function',
  'window.CONFIG_JS_CALLBACK is not a function',
  /ERROR BOUNDARY.*Failed to load chunk/,
  /ERROR BOUNDARY.*The object can not be found here/,
  /ERROR BOUNDARY.*Node\.(insertBefore|removeChild)/,
  /^Converting circular structure to JSON$/,
  'o.open is not a function',
];

const IGNORED_URLS = [
  // Ignore all non-benzinga URLs
  /^(?!https?:\/\/([a-zA-Z0-9-]+\.)*benzinga\.com(?=\/|:|$)).+$/,
  // Still specifically include known problematic benzinga URLs
  /^https:\/\/www\.benzinga\.com\/_next\/data\/.*\.json(\?.*)?$/,
  /^https:\/\/accounts\.benzinga\.com\/api\/v1\/touch/,
];

const RUM_EVENT_TYPES_DROPPED_IN_LOGS = new Set(['resources', 'longtask', 'internal']);

function messageMatchesIgnoredPattern(message: string): boolean {
  return IGNORED_ERRORS.some(pattern =>
    typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message),
  );
}

function shouldDropRumMessage(message: string | undefined): boolean {
  if (!message) return false;
  return messageMatchesIgnoredPattern(message) || /^ResizeObserver loop/.test(message);
}

// https://coralogix.com/docs/rum-integration-package/
// https://coralogix.com/docs/real-user-monitoring/
// https://www.npmjs.com/package/@coralogix/browser
export const CoralogixRumLoader = (user: User | undefined) => {
  if (process.env.NODE_ENV !== 'production') return;
  return import('@coralogix/browser').then(module => {
    module.CoralogixRum.init({
      application: 'benzinga-next',
      beforeSend: event => {
        if (!event) return null;

        const rumEventType = event.event_context?.type;
        if (rumEventType && RUM_EVENT_TYPES_DROPPED_IN_LOGS.has(rumEventType)) {
          return null;
        }

        if (rumEventType === 'error' && event.snapshot_context && !event.error_context?.error_message) {
          return null;
        }

        if (event.error_context?.error_message?.includes('"error": {}')) {
          return null;
        }

        if (shouldDropRumMessage(event.error_context?.error_message)) {
          return null;
        }

        if (rumEventType === 'log') {
          const logMessage = event.log_context?.message;
          const boundaryErrorMessage =
            typeof event.log_context?.data?.message === 'string' ? event.log_context.data.message : undefined;
          if (shouldDropRumMessage(logMessage) || shouldDropRumMessage(boundaryErrorMessage)) {
            return null;
          }
        }

        const stackFrames = event.error_context?.original_stacktrace;
        if (
          stackFrames?.some(
            stackFrame =>
              stackFrame.fileName?.includes('chrome-extension://') ||
              stackFrame.fileName?.includes('https://www.googletagmanager.com'),
          )
        ) {
          return null;
        }

        return event;
      },
      coralogixDomain: 'US2',
      environment: process.env.NODE_ENV || 'staging',
      ignoreErrors: IGNORED_ERRORS,
      ignoreUrls: IGNORED_URLS,
      labels: {
        git_commit: process.env.GIT_COMMITHASH || '',
      },
      public_key: process.env.CORALOGIX_RUM_PUBLIC_KEY || '',
      sessionConfig: {
        alwaysTrackSessionsWithErrors: true,
        keepSessionAfterReload: true,
        sessionSampleRate: 2,
      },
      sessionRecordingConfig: {
        autoStartSessionRecording: true, // Automatically records your session when SDK is up.
        enable: true,
        recordConsoleEvents: false,
        sessionRecordingSampleRate: 1,
        //   blockClass: 'rr-block', // Use a string or RegExp to redact all elements that contain this class, defaults to rr-block.
        //   ignoreClass: 'rr-ignore', // Use a string or RegExp to Ignore all events that contain this class, defaults to rr-ignore.
        //   maskTextClass: 'rr-mask', // Use a string or RegExp to mask all elements that contain this class, defaults to rr-mask.
        //   maskAllInputs: false, // Mask all input content as * (Default false), refer to Input types.
        //   maskInputOptions: { password: true }, // Mask some kinds of input as *, By Default the SDK masking password inputs.
      },
      traceParentInHeader: {
        enabled: true,
      },
      user_context: user
        ? {
            user_email: user.email,
            user_id: String(user.benzingaUid),
            user_metadata: {
              access_type: user.accessType,
            },
            user_name: user.displayName,
          }
        : undefined,
      version: process.env.RELEASE_VERSION || '',
    });
  });
};
